import { t } from '@ankr.com/common';

import { getEndpointName } from 'modules/endpoints/utils/getEndpointName';
import { AccountPermission } from 'modules/permissions/constants';
import { ChainGroupID, EndpointGroup } from 'modules/endpoints/types';
import { Placeholder } from 'modules/common/components/Placeholder';
import { UpgradeAccountDialog } from 'modules/common/components/UpgradeAccountDialog';
import { useAccount } from 'modules/account/hooks/useAccount';
import { useAccountStatus } from 'modules/accountStatus/hooks/useAccountStatus';
import { useAuth } from 'modules/auth/hooks/useAuth';
import { useChainSelectors } from 'modules/chains/components/ChainSelectorsProvider';
import { useCheckAccountPermission } from 'modules/account/hooks/useCheckAccountPermission';
import { useDialog } from 'modules/common/hooks/useDialog';

import { EndpointPlaceholder } from '../EndpointPlaceholder';
import { EndpointsHeader } from '../EndpointsHeader';
import { WSEndpoints } from '../WSEndpoints';

interface IWsFeatureEndpointsProps {
  title: string;
  group: EndpointGroup;
  wss: string[];
}

export const WsFeatureEndpoints = ({
  group,
  title,
  wss,
}: IWsFeatureEndpointsProps) => {
  const hasWSEndpointsAccess = useCheckAccountPermission({
    permission: AccountPermission.ChainItem,
  });

  const {
    isOpened: isPromoDialogOpened,
    onClose: onPromoDialogClose,
    onOpen: onPromoDialogOpen,
  } = useDialog();

  const { isLoggedIn } = useAuth();
  const { isDev } = useAccount();
  const { isPremium } = useAccountStatus();
  const { chainCallProtocol } = useChainSelectors();

  const isGRPC = chainCallProtocol === 'grpc';

  const hasNoAccess = isLoggedIn && !hasWSEndpointsAccess;

  if (hasNoAccess || isGRPC) {
    return null;
  }

  const header = <EndpointsHeader title={title} />;

  const isCustomEndpoints = group.id === ChainGroupID.MERGED;

  const getTitle = (wssEndpoint: string) => {
    return `WSS ${getEndpointName(group.urls, wssEndpoint)}`;
  };

  if (isCustomEndpoints && wss.length > 0) {
    return (
      <>
        {wss.map(wssEndpoint => (
          <WSEndpoints
            key={wssEndpoint}
            title={<EndpointsHeader title={getTitle(wssEndpoint) || title} />}
            wss={[wssEndpoint]}
          />
        ))}
      </>
    );
  }

  return (
    <Placeholder
      hasPlaceholder={isPremium}
      placeholder={<WSEndpoints title={header} wss={wss} />}
    >
      <Placeholder hasPlaceholder={isDev} placeholder={null}>
        <EndpointPlaceholder
          lockedLabel={t('chain-item.get-started.endpoints.lockedLabelWss')}
          onClick={onPromoDialogOpen}
          title={header}
        />
        <UpgradeAccountDialog
          isOpened={isPromoDialogOpened}
          onClose={onPromoDialogClose}
        />
      </Placeholder>
    </Placeholder>
  );
};
